// BillingPage — Stripe dashboard for admins; credits-only for members.
import { useEffect, useState } from 'react';
import { useOrgPageMeta, useOrgSlug } from '@/layouts/orgSettingsMeta';
import { billingService } from '@/services/api';
import type { BillingBody } from '@/services/api/billingService';
import { Button, Spinner } from '@/components/ui';
import { useModal } from '@/hooks/useModal';
import BillingStripeBody from '@/components/billing/BillingStripeBody';
import PlansTopupsModal, { type PlansTopupsMode } from '@/features/billing/PlansTopupsModal';
import OrgSectionHeading from '@/features/organization/OrgSectionHeading';

export default function BillingPage() {
  const slug = useOrgSlug();
  useOrgPageMeta('Billing', 'Subscription, credits, and payment methods.');

  const [data, setData] = useState<BillingBody | null>(null);
  const [loading, setLoading] = useState(true);
  const catalogModal = useModal();
  const [catalogMode, setCatalogMode] = useState<PlansTopupsMode>('catalog');

  const openCatalog = (mode: PlansTopupsMode) => {
    setCatalogMode(mode);
    catalogModal.show();
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    billingService
      .getBillingBody(slug)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading || !data) {
    return (
      <div className="org-tab-loading">
        <Spinner />
      </div>
    );
  }

  const isAdmin = data.isAdmin;
  const { balance } = data;

  return (
    <div className="org-panel">
      <OrgSectionHeading
        plain
        title={isAdmin ? 'Plans & usage' : 'Credits'}
        actions={
          isAdmin ? (
            <Button pill onClick={() => openCatalog('catalog')}>
              <i className="bi bi-tag me-1" /> View plans & top-ups
            </Button>
          ) : undefined
        }
      />
      {isAdmin ? (
        <BillingStripeBody
          data={data}
          onUpgradePlan={() => openCatalog('catalog')}
          onBuyCredits={() => openCatalog('topups')}
        />
      ) : (
        <div className="org-metrics is-two">
          <div className="org-metric-card">
            <div className="org-metric-label">Plan credits</div>
            <div className="org-metric-value">{balance?.planBalance ?? '—'}</div>
            <div className="org-metric-sub">
              Reserved: {balance?.reserved ?? 0} · 1 credit = 10 companies
            </div>
          </div>
          <div className="org-metric-card">
            <div className="org-metric-label">Top-up credits</div>
            <div className="org-metric-value">{balance?.topupBalance ?? '—'}</div>
            <div className="org-metric-sub">Purchased outside the plan</div>
          </div>
        </div>
      )}
      <PlansTopupsModal open={catalogModal.open} onClose={catalogModal.close} mode={catalogMode} />
    </div>
  );
}
